// Added by Claude for validation CRUD API service on 2025-11-09 22:01:00
// Updated by Claude on 2025-11-10 - Replaced localStorage with Firestore API calls
// Updated by Claude on 2025-12-28 - Use backend API for all validation operations
// Purpose: Service layer for managing custom business validations
// Features: CRUD operations to jayram.{appName}_validations

import { createLogger } from "../utils/logger";

const logger = createLogger("src/services/validationService.js");
const API_BASE_URL = 
  import.meta.env?.VITE_API_BASE || 
  process.env?.REACT_APP_API_BASE || 
  window.__API_BASE__ || 
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

/**
 * Get all custom validations for an app
 * @param {string} appName - Application name
 * @param {string} dbName - Database name (default: "jayram")
 * @returns {Promise<Array>} Array of validation objects
 */
export const getAllValidations = async (appName, dbName = "jayram") => {
  logger.entry("getAllValidations", "Fetching all validations", { appName, dbName });
  
  if (!appName) {
    logger.error("getAllValidations", "appName is required");
    throw new Error("appName is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validations/${dbName}/${appName}`;
    logger.info("getAllValidations", `Fetching from ${url}`);
    
    const response = await fetch(url, {
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const validations = await response.json();
    
    logger.info("getAllValidations", "Validations fetched", { count: validations.length });
    logger.exit("getAllValidations", { count: validations.length });
    
    return validations;
  } catch (error) {
    logger.error("getAllValidations", "Failed to fetch validations", error);
    throw error;
  }
};

/**
 * Get validation by ID
 * @param {string} appName - Application name
 * @param {string} validationId - Validation ID
 * @param {string} dbName - Database name (default: "jayram")
 * @returns {Promise<Object|null>} Validation object or null
 */
export const getValidationById = async (appName, validationId, dbName = "jayram") => {
  logger.entry("getValidationById", "Fetching validation", { appName, validationId, dbName });
  
  if (!appName || !validationId) {
    logger.error("getValidationById", "appName and validationId are required");
    throw new Error("appName and validationId are required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validations/${dbName}/${appName}/${validationId}`;
    logger.info("getValidationById", `Fetching from ${url}`);
    
    const response = await fetch(url, {
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        logger.warn("getValidationById", "Validation not found", { validationId });
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const validation = await response.json();
    
    logger.exit("getValidationById", { found: !!validation });
    return validation;
  } catch (error) {
    logger.error("getValidationById", "Failed to fetch validation", error);
    throw error;
  }
};

/**
 * Create new validation
 * @param {string} appName - Application name
 * @param {Object} validationData - Validation data
 * @param {string} dbName - Database name (default: "jayram")
 * @returns {Promise<Object>} Created validation
 */
export const createValidation = async (appName, validationData, dbName = "jayram") => {
  logger.entry("createValidation", "Creating validation", { appName, name: validationData.name, dbName });
  
  if (!appName) {
    logger.error("createValidation", "appName is required");
    throw new Error("appName is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validations/${dbName}/${appName}`;
    logger.info("createValidation", `POST to ${url}`);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(validationData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const created = await response.json();
    
    logger.info("createValidation", "Validation created", { validationId: created.id });
    logger.exit("createValidation", { validationId: created.id });
    
    return created;
  } catch (error) {
    logger.error("createValidation", "Failed to create validation", error);
    throw error;
  }
};

/**
 * Update existing validation
 * @param {string} appName - Application name
 * @param {string} validationId - Validation ID
 * @param {Object} updates - Updated validation data
 * @param {string} dbName - Database name (default: "jayram")
 * @returns {Promise<Object>} Updated validation
 */
export const updateValidation = async (appName, validationId, updates, dbName = "jayram") => {
  logger.entry("updateValidation", "Updating validation", { appName, validationId, dbName });
  
  if (!appName || !validationId) {
    logger.error("updateValidation", "appName and validationId are required");
    throw new Error("appName and validationId are required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validations/${dbName}/${appName}/${validationId}`;
    logger.info("updateValidation", `PUT to ${url}`);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const updated = await response.json();
    
    logger.info("updateValidation", "Validation updated", { validationId, newVersion: updated.version });
    logger.exit("updateValidation", { validationId });
    
    return updated;
  } catch (error) {
    logger.error("updateValidation", "Failed to update validation", error);
    throw error;
  }
};

/**
 * Delete validation
 * @param {string} appName - Application name
 * @param {string} validationId - Validation ID
 * @param {string} dbName - Database name (default: "jayram")
 * @returns {Promise<boolean>} Success status
 */
export const deleteValidation = async (appName, validationId, dbName = "jayram") => {
  logger.entry("deleteValidation", "Deleting validation", { appName, validationId, dbName });
  
  if (!appName || !validationId) {
    logger.error("deleteValidation", "appName and validationId are required");
    throw new Error("appName and validationId are required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validations/${dbName}/${appName}/${validationId}`;
    logger.info("deleteValidation", `DELETE to ${url}`);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    logger.info("deleteValidation", "Validation deleted", { validationId });
    logger.exit("deleteValidation", { success: true });
    
    return true;
  } catch (error) {
    logger.error("deleteValidation", "Failed to delete validation", error);
    throw error;
  }
};

/**
 * Get validations by type
 * @param {string} appName - Application name
 * @param {string} type - Validation type (visibility, required, etc.)
 * @param {string} dbName - Database name (default: "jayram")
 * @returns {Promise<Array>} Filtered validations
 */
export const getValidationsByType = async (appName, type, dbName = "jayram") => {
  logger.entry("getValidationsByType", "Fetching validations by type", { appName, type, dbName });
  
  try {
    const validations = await getAllValidations(appName, dbName);
    const filtered = validations.filter(v => v.type === type);
    
    logger.exit("getValidationsByType", { count: filtered.length });
    return filtered;
  } catch (error) {
    logger.error("getValidationsByType", "Failed to fetch validations by type", error);
    throw error;
  }
};

/**
 * Check if validation is in use
 * @param {string} validationId - Validation ID
 * @param {Array} forms - Array of form configs to check
 * @returns {Promise<Object>} Usage info { inUse: boolean, forms: [] }
 */
export const checkValidationUsage = async (validationId, forms = []) => {
  logger.entry("checkValidationUsage", "Checking validation usage", { validationId });
  
  try {
    const usedInForms = [];
    
    for (const form of forms) {
      if (form.fields) {
        for (const field of form.fields) {
          if (field.advancedOptions?.additionalValidations) {
            const hasValidation = field.advancedOptions.additionalValidations.some(
              v => v.id === validationId || v._validationRef?.validationId === validationId
            );
            
            if (hasValidation) {
              usedInForms.push({
                formId: form.id,
                formTitle: form.title,
                fieldLabel: field.label
              });
            }
          }
        }
      }
    }
    
    logger.exit("checkValidationUsage", { inUse: usedInForms.length > 0, count: usedInForms.length });
    
    return {
      inUse: usedInForms.length > 0,
      forms: usedInForms
    };
  } catch (error) {
    logger.error("checkValidationUsage", "Failed to check validation usage", error);
    throw error;
  }
};

// ====================================
// TEMPLATE FUNCTIONS
// Added by Claude on 2025-11-16
// ====================================

/**
 * Get all system validation templates
 * @returns {Promise<Array>} Array of template objects
 */
export const getAllTemplates = async () => {
  logger.entry("getAllTemplates", "Fetching all templates");
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates`;
    logger.info("getAllTemplates", `Fetching from ${url}`);
    
    const response = await fetch(url, {
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const templates = await response.json();
    
    logger.info("getAllTemplates", "Templates fetched", { count: templates.length });
    logger.exit("getAllTemplates", { count: templates.length });
    
    return templates;
  } catch (error) {
    logger.error("getAllTemplates", "Failed to fetch templates", error);
    throw error;
  }
};

/**
 * Get template by ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object|null>} Template object or null
 */
export const getTemplateById = async (templateId) => {
  logger.entry("getTemplateById", "Fetching template", { templateId });
  
  if (!templateId) {
    logger.error("getTemplateById", "templateId is required");
    throw new Error("templateId is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates/${templateId}`;
    logger.info("getTemplateById", `Fetching from ${url}`);
    
    const response = await fetch(url, {
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        logger.warn("getTemplateById", "Template not found", { templateId });
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const template = await response.json();
    
    logger.exit("getTemplateById", { found: !!template });
    return template;
  } catch (error) {
    logger.error("getTemplateById", "Failed to fetch template", error);
    throw error;
  }
};

/**
 * Create new template
 * @param {Object} templateData - Template data
 * @returns {Promise<Object>} Created template
 */
export const createTemplate = async (templateData) => {
  logger.entry("createTemplate", "Creating template", { templateName: templateData.templateName });
  
  if (!templateData.templateName) {
    logger.error("createTemplate", "templateName is required");
    throw new Error("templateName is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates`;
    logger.info("createTemplate", `Posting to ${url}`);
    
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(templateData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const created = await response.json();
    
    logger.info("createTemplate", "Template created", { id: created.id });
    logger.exit("createTemplate", { id: created.id });
    
    return created;
  } catch (error) {
    logger.error("createTemplate", "Failed to create template", error);
    throw error;
  }
};

/**
 * Update template
 * @param {string} templateId - Template ID
 * @param {Object} updates - Template updates
 * @returns {Promise<Object>} Updated template
 */
export const updateTemplate = async (templateId, updates) => {
  logger.entry("updateTemplate", "Updating template", { templateId });
  
  if (!templateId) {
    logger.error("updateTemplate", "templateId is required");
    throw new Error("templateId is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates/${templateId}`;
    logger.info("updateTemplate", `Putting to ${url}`);
    
    const response = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const updated = await response.json();
    
    logger.info("updateTemplate", "Template updated", { id: templateId, version: updated.version });
    logger.exit("updateTemplate", { id: templateId });
    
    return updated;
  } catch (error) {
    logger.error("updateTemplate", "Failed to update template", error);
    throw error;
  }
};

/**
 * Delete template
 * @param {string} templateId - Template ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteTemplate = async (templateId) => {
  logger.entry("deleteTemplate", "Deleting template", { templateId });
  
  if (!templateId) {
    logger.error("deleteTemplate", "templateId is required");
    throw new Error("templateId is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates/${templateId}`;
    logger.info("deleteTemplate", `Deleting from ${url}`);
    
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    logger.info("deleteTemplate", "Template deleted", { templateId });
    logger.exit("deleteTemplate", { success: true });
    
    return true;
  } catch (error) {
    logger.error("deleteTemplate", "Failed to delete template", error);
    throw error;
  }
};

/**
 * Increment template usage count
 * @param {string} templateId - Template ID
 * @returns {Promise<number>} New usage count
 */
export const incrementTemplateUsage = async (templateId) => {
  logger.entry("incrementTemplateUsage", "Incrementing usage", { templateId });
  
  if (!templateId) {
    logger.error("incrementTemplateUsage", "templateId is required");
    throw new Error("templateId is required");
  }
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates/${templateId}/use`;
    logger.info("incrementTemplateUsage", `Posting to ${url}`);
    
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    logger.info("incrementTemplateUsage", "Usage incremented", { templateId, count: result.usageCount });
    logger.exit("incrementTemplateUsage", { count: result.usageCount });
    
    return result.usageCount;
  } catch (error) {
    logger.error("incrementTemplateUsage", "Failed to increment usage", error);
    throw error;
  }
};

/**
 * Initialize default system templates
 * @returns {Promise<Object>} Initialization results
 */
export const initializeDefaultTemplates = async () => {
  logger.entry("initializeDefaultTemplates", "Initializing default templates");
  
  try {
    const url = `${API_BASE_URL}/api/validation-templates/init-defaults/seed`;
    logger.info("initializeDefaultTemplates", `Posting to ${url}`);
    
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    logger.info("initializeDefaultTemplates", "Templates initialized", { results: result.results });
    logger.exit("initializeDefaultTemplates", { success: true });
    
    return result;
  } catch (error) {
    logger.error("initializeDefaultTemplates", "Failed to initialize templates", error);
    throw error;
  }
};

// End of validation service
// Updated by Claude on 2025-11-10 - Converted from localStorage to Firestore API
// Updated by Claude on 2025-11-16 - Added template functions

export default {
  getAllValidations,
  getValidationById,
  createValidation,
  updateValidation,
  deleteValidation,
  getValidationsByType,
  checkValidationUsage,
  // Template functions
  getAllTemplates,
  getTemplateById,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  incrementTemplateUsage,
  initializeDefaultTemplates
};

